import React, { useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useCreateSource, useGetSasUrl } from "../../hooks/useApi";
import { useTranslation } from "../../hooks/useTranslation";
import { api } from "../../api/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import {
  Upload,
  FileText,
  X,
  AlertTriangle,
  CheckCircle,
  Loader2,
} from "lucide-react";

const ESTENSIONI = ["pdf", "txt", "md"];
const estensione = (file) => (file.name.split(".").pop() || "").toLowerCase();
// Gli stessi limiti del testo lato API (sourceText: da 10 a 100.000 caratteri).
const TESTO_MIN = 10;
const TESTO_MAX = 100000;

const UploadSourceDialog = ({ isOpen, onOpenChange, botId, onSuccess }) => {
  const { t } = useTranslation();
  const createSourceMutation = useCreateSource();
  const getSasUrlMutation = useGetSasUrl();

  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState("idle"); // idle, uploading, success, error
  const [uploadError, setUploadError] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm({
    defaultValues: {
      filename: "",
    },
  });

  // Validate file type and size. Il tipo si legge dall'estensione: su Windows
  // i .md arrivano spesso senza tipo. Il server legge come documento solo i
  // PDF; .txt e .md diventano fonti di testo (come nell'onboarding). Word non
  // lo legge nessuno: non si accetta.
  const validateFile = (file) => {
    const maxSize = 50 * 1024 * 1024; // 50MB

    if (file.size > maxSize) {
      throw new Error(
        t("dialogs.upload.errorFileTooLarge", "File size must be less than 50MB")
      );
    }

    if (!ESTENSIONI.includes(estensione(file))) {
      throw new Error(
        t(
          "dialogs.upload.errorFileTypeNotSupported",
          "File type not supported. Please upload a PDF, TXT or MD file."
        )
      );
    }

    return true;
  };

  // Handle file selection
  const handleFileSelect = useCallback(
    (file) => {
      try {
        validateFile(file);
        setSelectedFile(file);
        setValue("filename", file.name);
        setUploadError(null);
        setUploadStatus("idle");
        setUploadProgress(0);
      } catch (error) {
        setUploadError(error.message);
        setSelectedFile(null);
        setValue("filename", "");
      }
    },
    [setValue]
  );

  // Handle drag and drop
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragOver(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        handleFileSelect(files[0]);
      }
    },
    [handleFileSelect]
  );

  // Handle file input change
  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  // Remove selected file
  const removeFile = () => {
    setSelectedFile(null);
    setValue("filename", "");
    setUploadError(null);
    setUploadStatus("idle");
    setUploadProgress(0);
  };

  // Upload file to Azure Blob Storage
  const uploadFile = async (file) => {
    try {
      setUploadStatus("uploading");
      setUploadProgress(0);

      // Get SAS URL from backend (qui arrivano solo PDF)
      const sasResponse = await getSasUrlMutation.mutateAsync({
        filename: file.name,
        contentType: "application/pdf",
        botId,
      });

      // Upload file directly to Azure Blob Storage
      await api.uploads.uploadFile(file, sasResponse.uploadUrl, (progress) =>
        setUploadProgress(progress)
      );

      setUploadStatus("success");
      return sasResponse.blobPath;
    } catch (error) {
      setUploadStatus("error");
      setUploadError(
        error.message || t("dialogs.upload.uploadFailed", "Upload failed")
      );
      throw error;
    }
  };

  // Handle form submission
  const onSubmit = async (data) => {
    if (!selectedFile) {
      setUploadError(
        t("dialogs.upload.errorNoFileSelected", "Please select a file to upload")
      );
      return;
    }

    // Il nome visualizzato scelto, altrimenti quello del file.
    const nome = data.filename?.trim() || selectedFile.name;

    try {
      if (estensione(selectedFile) === "pdf") {
        // Upload file first
        const blobPath = await uploadFile(selectedFile);

        // Create source record: per i PDF caricati il nome del file e' solo
        // l'etichetta (il file e' blob_path), quindi porta il nome scelto.
        await createSourceMutation.mutateAsync({
          botId,
          data: {
            type: "pdf",
            filename: nome,
            blob_path: blobPath,
          },
        });
      } else {
        // .txt e .md: il testo si legge qui e diventa una fonte di testo.
        const testo = await selectedFile.text();
        if (testo.trim().length < TESTO_MIN) {
          setUploadError(t("dialogs.upload.errorTextTooShort", "The file is empty or too short."));
          return;
        }
        if (testo.length > TESTO_MAX) {
          setUploadError(t("dialogs.upload.errorTextTooLong", "The file is too long: up to 100,000 characters."));
          return;
        }
        await createSourceMutation.mutateAsync({
          botId,
          data: { type: "text", title: nome, inline_text: testo },
        });
      }

      // Reset form and close dialog
      reset();
      removeFile();
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error("Failed to create source:", error);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // Reset form when dialog closes
  const handleOpenChange = (open) => {
    if (!open) {
      reset();
      removeFile();
    }
    onOpenChange(open);
  };

  const isSubmitting =
    uploadStatus === "uploading" || createSourceMutation.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("dialogs.upload.title", "Upload File")}</DialogTitle>
          <DialogDescription>
            {t(
              "dialogs.upload.description",
              "Upload a PDF or a text file (.txt, .md) to add to your bot's knowledge base."
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* File Upload Area */}
          <div className="space-y-4">
            <Label>{t("dialogs.upload.fileUploadLabel", "File Upload")}</Label>

            {!selectedFile ? (
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  isDragOver
                    ? "border-primary bg-primary/5"
                    : "border-muted-foreground/25 hover:border-muted-foreground/50"
                }`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <Upload className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <div className="space-y-2">
                  <p className="text-lg font-medium">
                    {t("dialogs.upload.dropZoneTitle", "Drop your file here")}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t(
                      "dialogs.upload.dropZoneSubtitle",
                      "or click to browse files"
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t(
                      "dialogs.upload.dropZoneHint",
                      "Supports PDF, TXT and MD files up to 50MB"
                    )}
                  </p>
                </div>
                <Input
                  type="file"
                  className="hidden"
                  accept=".pdf,.txt,.md"
                  onChange={handleFileInputChange}
                  id="file-upload-dialog"
                />
                <Button
                  type="button"
                  variant="outline"
                  className="mt-4"
                  onClick={() =>
                    document.getElementById("file-upload-dialog").click()
                  }
                >
                  {t("dialogs.upload.browseButton", "Browse Files")}
                </Button>
              </div>
            ) : (
              <div className="border rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <FileText className="h-8 w-8 text-blue-600 flex-shrink-0 mt-1" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {selectedFile.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatFileSize(selectedFile.size)} •{" "}
                        {selectedFile.type}
                      </p>

                      {/* Upload Progress */}
                      {uploadStatus === "uploading" && (
                        <div className="mt-2">
                          <div className="flex items-center space-x-2">
                            <div className="flex-1 bg-muted rounded-full h-2">
                              <div
                                className="bg-primary h-2 rounded-full transition-all duration-300"
                                style={{ width: `${uploadProgress}%` }}
                              />
                            </div>
                            <span className="text-xs text-muted-foreground">
                              {uploadProgress}%
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Upload Status */}
                      {uploadStatus === "success" && (
                        <div className="flex items-center space-x-1 mt-2">
                          <CheckCircle className="h-4 w-4 text-green-600" />
                          <span className="text-xs text-green-600">
                            {t(
                              "dialogs.upload.uploadComplete",
                              "Upload complete"
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {uploadStatus !== "uploading" && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={removeFile}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            )}

            {/* Error Display */}
            {uploadError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{uploadError}</p>
                </div>
              </div>
            )}

            {/* Mutation Error */}
            {createSourceMutation.error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">
                    {createSourceMutation.error.message}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* File Name Input (Optional Override) */}
          {selectedFile && (
            <div className="space-y-2">
              <Label htmlFor="filename">
                {t("dialogs.upload.displayNameLabel", "Display Name (Optional)")}
              </Label>
              <Input
                id="filename"
                {...register("filename")}
                placeholder={t(
                  "dialogs.upload.displayNamePlaceholder",
                  "Enter a custom name for this file"
                )}
                disabled={isSubmitting}
              />
              <p className="text-xs text-muted-foreground">
                {t(
                  "dialogs.upload.displayNameHint",
                  "Leave empty to use the original filename"
                )}
              </p>
            </div>
          )}

          {/* File Type Info */}
          <div className="bg-muted/30 rounded-lg p-4">
            <h4 className="text-sm font-medium mb-2">
              {t("dialogs.upload.supportedTypesTitle", "Supported File Types")}
            </h4>
            <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
              <div>{t("dialogs.upload.typePdf", "• PDF documents (.pdf)")}</div>
              <div>{t("dialogs.upload.typeText", "• Text files (.txt)")}</div>
              <div>
                {t("dialogs.upload.typeMarkdown", "• Markdown files (.md)")}
              </div>
              <div>
                {t("dialogs.upload.typeWord", "• Word: save it as PDF first")}
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {t("dialogs.upload.maxFileSize", "Maximum file size: 50MB")}
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isSubmitting}
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button type="submit" disabled={isSubmitting || !selectedFile}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {uploadStatus === "uploading"
                    ? t("dialogs.upload.uploading", "Uploading...")
                    : t("dialogs.upload.creating", "Creating...")}
                </>
              ) : (
                t("dialogs.upload.title", "Upload File")
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UploadSourceDialog;
